import { useEffect, useState } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useRouter } from 'next/compat/router';
import { useIdleTimerContext } from 'react-idle-timer';
import { useApolloClient } from '@apollo/client';

import type { Dispensaries } from 'src/gql/graphql';
import useUI from 'src/hooks/use-ui';
import useUser from 'src/hooks/use-user';
import useCart from 'shared/hooks/use-cart';
import { useModals } from 'src/components/modals/use-modals';
import { useGetSessionId } from '../kiosk-pro-shared/use-session-id';
import { useCreatePublicContextSession } from '../kiosk-pro-shared/use-create-public-context-session';
import { teardownKioskSession } from '../kiosk-pro-shared/kiosk-session-teardown';

const CONSUMER_LOGIN_PATHS = ['login/public-session-login', 'login/public-session-loyalty-signup'];

type UseKioskProLayoutProps = {
  dispensary: Dispensaries;
};

type UseKioskProLayoutReturn = {
  isConsumerPhoneLoginPage: boolean;
  startNewSession: () => void;
  goToCart: () => void;
  kioskProModalLogin: () => Promise<void>;
  createPublicContextSessionLoading: boolean;
  isKioskProLoginModalOpen: boolean;
  publicLoginSessionId: string;
  setIsKioskProLoginModalOpen: (open: boolean) => void;
  consumerPhoneLoginUrl: string;
  isEndSessionModalOpen: boolean;
  setIsEndSessionModalOpen: (open: boolean) => void;
  confirmedStartNewSession: () => Promise<void>;
};

export const useKioskProLayout = ({ dispensary }: UseKioskProLayoutProps): UseKioskProLayoutReturn => {
  const { setIsKioskProLoginModalOpen, isKioskProLoginModalOpen, dispensaryRootUrl, openCartDrawer } = useUI();
  const User = useUser();
  const router = useRouter();
  const Cart = useCart();
  const modals = useModals();
  const apolloClient = useApolloClient();
  const cName = dispensary.cName ?? '';
  const dispensaryId = dispensary.id;
  const disableFasterCheckout = dispensary.orderTypesConfigV2?.kiosk?.disableFasterCheckout ?? false;
  const idleTimer = useIdleTimerContext();
  // useObserver (not observer()) stays here: this hook is also consumed by
  // kiosk-logged-out-empty-cart.tsx, which is not wrapped in observer(), so it relies on
  // this call to re-render on User.isLoggedIn changes.
  useObserver(() => User?.isLoggedIn);

  const [consumerPhoneLoginUrl, setConsumerPhoneLoginUrl] = useState('');
  const [isEndSessionModalOpen, setIsEndSessionModalOpen] = useState(false);
  const publicLoginSessionId = useGetSessionId();
  const { createPublicContextSession, isLoading: createPublicContextSessionLoading } = useCreatePublicContextSession();

  // router.pathname is plain React state, not a MobX observable, so no observer wrapper is needed here.
  const isConsumerPhoneLoginPage = CONSUMER_LOGIN_PATHS.some((path) => router?.pathname.includes(path));

  useEffect(() => {
    if (typeof window !== 'undefined' && cName) {
      setConsumerPhoneLoginUrl(
        `${window.location.origin}/kiosks/${cName}/login/public-session-login?session-id=${publicLoginSessionId}`
      );
    }
  }, [cName, publicLoginSessionId]);

  const startNewSession = (): void => {
    setIsEndSessionModalOpen(true);
  };

  const confirmedStartNewSession = async (): Promise<void> => {
    try {
      setIsEndSessionModalOpen(false);
      await teardownKioskSession({
        user: User,
        cart: Cart,
        ui: { setIsKioskProLoginModalOpen },
        apolloClient,
        idleTimer,
        dispensaryId,
        modals,
      });

      void router?.push(
        `/${String(dispensaryRootUrl)}/[cName]/lock`,
        `/${String(dispensaryRootUrl)}/${String(cName)}/lock`
      );
    } catch (error) {
      console.error('Failed to start new session:', error);
    }
  };

  const goToCart = (): void => {
    openCartDrawer();
  };

  const kioskProModalLogin = async (): Promise<void> => {
    if (!dispensaryId || disableFasterCheckout) {
      return;
    }

    // ECOMM-10367: the hook persists the creator secret so the modal's display poll
    // can retrieve the JWT once the per-dispensary flag is on.
    const sessionId = await createPublicContextSession(dispensaryId);

    if (sessionId) {
      const currentQuery = { ...router?.query };
      currentQuery['session-id'] = sessionId;

      void router?.push(
        {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
          pathname: router?.pathname,
          query: currentQuery,
        },
        undefined,
        { scroll: false }
      );
    }

    setIsKioskProLoginModalOpen(true);
  };

  return {
    isConsumerPhoneLoginPage,
    startNewSession,
    goToCart,
    kioskProModalLogin,
    createPublicContextSessionLoading,
    isKioskProLoginModalOpen,
    publicLoginSessionId,
    setIsKioskProLoginModalOpen,
    consumerPhoneLoginUrl,
    isEndSessionModalOpen,
    setIsEndSessionModalOpen,
    confirmedStartNewSession,
  };
};
